'use client';

import type { AnswerState } from '@repo/contracts/cardfinder/funnel/types';
import { createContext, type TransitionStartFunction, useContext } from 'react';
import type { BranchId } from '@/shared/config/funnel/constants';
import type { FlattenedFunnelConfig } from '@/shared/config/funnel/flattened/types';
import type { SessionSyncResult } from '../api/sessionWriteResult';

/**
 * What the funnel can ask of session persistence. Lives below both features so
 * the funnel can call it without importing the session feature that provides it.
 */
export type SessionSync = {
  onAnswerSubmitted: (
    submittedAnswers: AnswerState,
    funnelConfigVersion: string,
  ) => Promise<SessionSyncResult>;
  completeRun: (
    funnelFlattenedConfig: FlattenedFunnelConfig,
    funnelConfigVersion: string,
    answers: AnswerState,
  ) => Promise<SessionSyncResult>;
  abandonRun: () => void;
  syncOnLogin: (
    branchId: BranchId | null,
    funnelConfigVersion: string,
    startFunnelTransition: TransitionStartFunction,
  ) => void;
};

export const SessionSyncContext = createContext<SessionSync | undefined>(undefined);

export function useSessionSync(): SessionSync {
  const ctx = useContext(SessionSyncContext);
  if (!ctx) throw new Error('useSessionSync must be used within SessionSyncProvider');
  return ctx;
}
